import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import TemporalIcon from "src/components/ui/icons/temporal";
import { getProductName } from "src/utils/brandTheme";

import TemporalIntegrationForm, { TemporalAuthTypeType } from "./TemporalIntegrationForm";

interface TemporalIntegrationProps {
  isOpen: boolean;
  onClose: () => void;
  onRefetch?: () => void;
  brandTheme?: string | null;
}

const ApiKeyInstructions: React.FC = () => (
  <div className="text-muted-foreground">
    <p className="mb-2 font-bold">API Key Instructions:</p>
    <ol className="text-sm space-y-2 list-decimal list-inside">
      <li>
        Log into your{" "}
        <a
          href="https://cloud.temporal.io"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:text-primary/80 underline"
        >
          Temporal Cloud account
        </a>
        .
      </li>
      <li>
        Create an account-level API key by following the{" "}
        <a
          href="https://docs.temporal.io/cloud/api-keys#generate-an-api-key"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:text-primary/80 underline"
        >
          API Key generation guide
        </a>
        . The key must have permission to list namespaces.
      </li>
      <li>
        Copy the generated API key and paste it into the API Key field below.
      </li>
    </ol>
  </div>
);

const MtlsInstructions: React.FC = () => (
  <div className="text-muted-foreground">
    <p className="mb-2 font-bold">Certificate (mTLS) Instructions:</p>
    <ol className="text-sm space-y-2 list-decimal list-inside">
      <li>
        Generate a client certificate and private key (PEM) for the namespace you want to
        connect. See the{" "}
        <a
          href="https://docs.temporal.io/cloud/certificates"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:text-primary/80 underline"
        >
          Manage certificates guide
        </a>
        .
      </li>
      <li>
        In the Temporal Cloud UI, open the target namespace and add the CA certificate so it
        trusts the client cert.
      </li>
      <li>
        From the namespace overview page, copy the namespace name (e.g.{" "}
        <code>my-ns.acct</code>) and the gRPC endpoint (e.g.{" "}
        <code>my-ns.acct.tmprl.cloud:7233</code>).
      </li>
      <li>
        Paste the PEM-encoded client cert, client private key, namespace name, and gRPC
        endpoint into the form below.
      </li>
      <li>
        <strong>Note:</strong> Temporal Cloud certificates are scoped to a single namespace.
      </li>
    </ol>
  </div>
);

const TemporalInstructions: React.FC<{ authType: TemporalAuthTypeType }> = ({ authType }) => (
  <div className="text-muted-foreground">
    {authType === "API_KEY" ? <ApiKeyInstructions /> : <MtlsInstructions />}
  </div>
);

export default function TemporalIntegration({ isOpen, onClose, onRefetch, brandTheme }: TemporalIntegrationProps) {
  const [showInstructions, setShowInstructions] = useState(false);
  const [authType, setAuthType] = useState<TemporalAuthTypeType>("API_KEY");
  const productName = getProductName(brandTheme);

  const handleFormComplete = (success: boolean) => {
    if (success) {
      onClose();
      onRefetch?.();
    }
  };

  const handleFormCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <TemporalIcon className="h-6 w-6" />
            Connect Temporal
          </DialogTitle>
          <DialogDescription>
            Connect your Temporal Cloud account to give {productName} access.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 overflow-y-auto max-h-[80vh] p-1">
          <div className="space-y-3">
            <button
              onClick={() => setShowInstructions(!showInstructions)}
              className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
            >
              {showInstructions ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {showInstructions ? "Hide instructions" : "Show instructions"}
            </button>

            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
              showInstructions
                ? "max-h-[2000px] opacity-100"
                : "max-h-0 opacity-0"
            }`}>
              <TemporalInstructions authType={authType} />
            </div>
          </div>

          <TemporalIntegrationForm
            onComplete={handleFormComplete}
            onCancel={handleFormCancel}
            onAuthTypeChange={setAuthType}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}